/**
 * Foliage wind (Graphics G4) — a TSL height-weighted sine sway `positionNode`.
 *
 * `createWindMaterial` (engine) is a no-op stub on WebGPU — the actual displacement is
 * the caller's `material.positionNode`. This applies it: vertices sway by a shared wind
 * uniform (dirX, dirZ, strength, time), tips swaying more than roots (height²).
 * WeatherModule writes `windVector` each frame; EnvironmentModule applies this to foliage.
 */
import * as THREE from 'three/webgpu';
import { Fn, positionLocal, positionGeometry, positionPrevious, positionWorld, sin, vec3, float, vec2, uniform, texture, step } from 'three/tsl';
import { trampleBoundsNode, trampleTexture } from './foliage_trample.js';

/** Shared wind state (x: dirX, y: dirZ, z: strength, w: time). Mutated by WeatherModule. */
export const windVector = new THREE.Vector4(1, 0, 0, 0);
const windNode = uniform(windVector);
/** The previous frame's distance in time (s) for the motion vectors — WeatherModule writes it each frame. */
export const windPrevDt = uniform(1 / 60);

export function applyWind(
  material: THREE.Material,
  opts?: { swayStrength?: number; freq?: number; maxHeight?: number; trample?: boolean; trampleStrength?: number },
): void {
  const strength = opts?.swayStrength ?? 0.12;
  const freq = opts?.freq ?? 1.5;
  const maxH = opts?.maxHeight ?? 1.0;
  // TRAMPLING (2026-09-15): blades bend away from whatever walked through
  // them. It lives HERE rather than in the engine's applyInfluenceToFoliage
  // because that modifier replaces `material.positionNode`, and this material
  // already owns one — two modifiers would silently mean "last one wins" and
  // the wind would vanish. One node, both displacements, one previous
  // position. lib/foliage_trample.ts owns the field and its bounds uniform.
  const trampleTex = opts?.trample ? trampleTexture() : null;
  const trampleK = opts?.trampleStrength ?? 1;
  const trampleNode = trampleTex ? texture(trampleTex) : null;
  const sway = Fn(() => {
    const p = positionLocal;
    // Phase from the WORLD position (includes the instance transform; small
    // numbers under the scene rebase): positionLocal is identical for every
    // instance of a species, so a whole field swayed as one rigid body.
    const w = positionWorld;
    // Height mask in GEOMETRY space (2026-09-15). This read `positionLocal.y`,
    // which three has already put through instancedMesh()/batch() by the time
    // a positionNode runs — for instanced grass that is the blade's WORLD
    // height, so `.clamp(0,1)` pinned every vertex at 1 and the whole blade
    // slid sideways as a rigid body instead of bending from its root. Same law
    // as HierarchicalWind and the bird wing hinge.
    const h = positionGeometry.y.div(maxH).clamp(0, 1);
    const hMask = h.mul(h); // tips move most, roots stay planted
    const k = windNode.z.mul(strength).mul(hMask);
    const at = (t: ReturnType<typeof float>) => {
      const ox = sin(t.mul(freq).add(w.x.mul(0.5)).add(w.z.mul(0.3))).mul(windNode.x).mul(k);
      const oz = sin(t.mul(freq).add(w.z.mul(0.5)).add(w.x.mul(0.3))).mul(windNode.y).mul(k);
      let off: { add(v: unknown): unknown } = vec3(ox, float(0), oz);
      if (trampleNode) {
        // Field UV from the blade's world XZ through the shared bounds
        // (minX, minZ, rangeX, rangeZ).
        const u = w.x.sub(trampleBoundsNode.x).div(trampleBoundsNode.z);
        const v = w.z.sub(trampleBoundsNode.y).div(trampleBoundsNode.w);
        const bend = (trampleNode as unknown as { sample: (uv: ReturnType<typeof vec2>) => { r: ReturnType<typeof float>; g: ReturnType<typeof float> } }).sample(vec2(u, v));
        // FIX (shader sweep C, 2026-10-06): the field is ClampToEdge, and its border texels are NOT zero whenever an agent
        // near the region's rim splats onto them — every blade past that rim, out to the ring's end, read the border
        // texel: a stripe of bent foliage along the row. Zero outside the region (the grass blades' own `inside` rule).
        const inside = step(0, u).mul(step(u, 1)).mul(step(0, v)).mul(step(v, 1)).mul(hMask).mul(trampleK);
        off = off.add(vec3(bend.r.mul(inside), float(0), bend.g.mul(inside))) as { add(v: unknown): unknown };
      }
      return p.add(off as never);
    };
    // Motion vectors (2026-09-09, "foliage looks glitchy with motion blur on
    // even when you are stationary"): three's positionPrevious defaults to the
    // undisplaced geometry, so the sway read as per-frame velocity and the
    // blur smeared every blade. Emit the sway ONE FRAME AGO as the previous
    // position — the blur follows the real motion only.
    positionPrevious.assign(at(windNode.w.sub(windPrevDt)));
    return at(windNode.w);
  });
  (material as unknown as { positionNode: unknown }).positionNode = sway();
}
